import "formatjs-intl-relativetimeformat/polyfill";

import { bridge } from "@multiweb/reactCore/bridge";
import { capitalize, invariant } from "@multiweb/reactUtils";

const { isToday, msPerDay } = bridge;

/**
 * Vytvoří textovou reprezentaci časového okamžiku vzniku notifikace.
 * V některých případech využívá třídu `RelativeTimeFormat`, která je v Safari nepodporovaná a je nutné použít polyfill.
 * @param createdDate Časový okamžik vytvoření notifikace
 * @param localeCode Kód jazyka aplikace
 */
export const createCreatedDateText = (createdDate: Date, localeCode: string): string => {
    invariant(Intl.RelativeTimeFormat, "Intl.RelativeTimeFormat is undefined. Make sure the polyfill is working correctly.");
    const now = new Date();
    const timestampDiff = now.getTime() - createdDate.getTime();
    const seconds = Math.floor(timestampDiff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    const rtlHelper = (options: Intl.RelativeTimeFormatOptions, shift: number, unit: Intl.RelativeTimeFormatUnit) => capitalize(
        new Intl.RelativeTimeFormat(localeCode, options).format(shift, unit),
    );

    const localeHelper = (options: Intl.DateTimeFormatOptions) => capitalize(createdDate.toLocaleString(localeCode, options));

    if (seconds < 60) {
        return rtlHelper({ style: "short", numeric: "auto" }, 0, "second");
    }
    if (minutes < 60) {
        return rtlHelper({ style: "short" }, -minutes, "minute");
    }
    if (hours <= 5) {
        return rtlHelper({ style: "short" }, -hours, "hour");
    }
    const time = localeHelper({ hour: "numeric", minute: "numeric" });
    if (isToday(createdDate)) {
        const todayLocalization = rtlHelper({ numeric: "auto" }, 0, "day");
        return `${todayLocalization} ${time}`;
    }
    // Posun o den a kontrola na dnešní datum
    if (isToday(new Date(createdDate.getTime() + msPerDay))) {
        const yesterdayLocalization = rtlHelper({ numeric: "auto" }, -1, "day");
        return `${yesterdayLocalization} ${time}`;
    }
    if (days <= 7) {
        const weekDayLocalization = localeHelper({ weekday: "short" });
        return `${weekDayLocalization} ${time}`;
    }
    if (createdDate.getFullYear() === now.getFullYear()) {
        return localeHelper({ month: "numeric", day: "numeric" });
    }

    return localeHelper({ month: "numeric", day: "numeric", year: "numeric" });
};
